import React, { useContext, useState, useRef, useEffect } from 'react';
import { ThemeContext } from '../context/ThemeContext';

const ThemeMenu = ({ isVisible, onClose, buttonRef }) => {
  const { theme, colorTheme, toggleTheme, changeColorTheme } = useContext(ThemeContext);
  const menuRef = useRef(null);
  
  // Position the menu below the theme button
  const [menuStyle, setMenuStyle] = useState({
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 1000,
    opacity: 0,
    visibility: 'hidden',
  });

  useEffect(() => {
    if (isVisible && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: buttonRect.bottom + 8,
        left: buttonRect.left - 200 + (buttonRect.width / 2), // Center menu under button
        zIndex: 1000,
        opacity: 1,
        visibility: 'visible',
        transition: 'opacity 0.2s ease-in-out',
      });
    } else {
      setMenuStyle(prev => ({
        ...prev,
        opacity: 0,
        visibility: 'hidden',
      }));
    }
  }, [isVisible, buttonRef]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible, onClose, buttonRef]);

  const themes = [
    { 
      name: 'default', 
      label: 'Default',
      dark: { primary: '#6e72c3', secondary: '#4b5563' },
      light: { primary: '#8b8cdf', secondary: '#94a3b8' }
    },
    { 
      name: 'grass', 
      label: 'Grass',
      dark: { primary: '#6ec386', secondary: '#4b634f' },
      light: { primary: '#498a63', secondary: '#94b8a1' }
    },
    { 
      name: 'wood', 
      label: 'Wood',
      dark: { primary: '#c39e6e', secondary: '#635e4b' },
      light: { primary: '#cc967b', secondary: '#b8b094' }
    },
    { 
      name: 'ocean', 
      label: 'Ocean',
      dark: { primary: '#0284c7', secondary: '#0369a1' },
      light: { primary: '#0ea5e9', secondary: '#7dd3fc' }
    },
    {
        name : 'mauve',
        label: 'Mauve',
        dark: { primary: '#c36e99', secondary: '#634b5d' },
        light: { primary: '#c2486f', secondary: '#b894aa' }  
    },
    {
        name: 'monotone',
        label: 'Monotone',
        dark: { primary: '#4d4d4d', secondary: '#6c6c6c' },
        light: { primary: '#222222', secondary: '#6c6c6c' }
    },
    {
        name: 'coral',
        label: 'Coral',
        dark: { primary: '#e57373', secondary: '#f48fb1' },
        light: { primary: '#cd5c5c', secondary: '#dc6b73' }
    },
    {
        name: 'mint',
        label: 'Mint',
        dark: { primary: '#8ac5c1', secondary: '#7db3b0' },
        light: { primary: '#7db3b0', secondary: '#6b9c98' }
    },
    {
        name: 'neon',
        label: 'Neon',
        dark: { primary: '#ff69b4', secondary: '#768bde' },
        light: { primary: '#ec4899', secondary: '#22d3ee' }
    },
    {
        name: 'honey',
        label: 'Honey',
        dark: { primary: '#ffc947', secondary: '#ffb74d' },
        light: { primary: '#ffb74d', secondary: '#ff9800' }
    }
  ];

  const currentThemeLabel = themes.find(t => t.name === colorTheme)?.label || 'Unknown';

  if (!isVisible) return null;

  return (
    <div 
      ref={menuRef}
      style={menuStyle}
      className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-lg p-4 w-64"
    >
      {/* Dark/Light Mode Toggle */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-[var(--text-primary)]">Dark Mode</span>
        <button
          onClick={toggleTheme}
          className={`relative inline-flex h-6 w-11 items-center rounded-full border border-[var(--text-primary)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 ${
            theme === 'dark' ? 'bg-[var(--primary)]' : 'bg-[var(--bg-primary)]'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-[var(--text-primary)] transition-transform ${
              theme === 'dark' ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Color Theme Selection */}
      <div className="border-t border-[var(--border-color)] pt-3">
        <h4 className="text-xs font-medium text-[var(--text-secondary)] mb-3">
          Color Theme ({currentThemeLabel})
        </h4>
        <div className="grid grid-cols-5 gap-2">
          {themes.map((t) => {
            const colors = theme === 'dark' ? t.dark : t.light;
            return (
              <button
                key={t.name}
                onClick={() => {
                  changeColorTheme(t.name);
                  // Don't close the menu when selecting a theme
                }}
                className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all hover:scale-110 ${
                  colorTheme === t.name 
                    ? 'ring-2 ring-offset-2 ring-[var(--primary)]' 
                    : 'opacity-70 hover:opacity-100'
                }`}
                style={{
                  background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`
                }}
                title={t.label}
              >
                {colorTheme === t.name && (
                  <span className="text-white text-xs font-bold drop-shadow-md">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ThemeMenu;
